import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, Loader2, Lock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Senha deve ter no mínimo 6 caracteres'); return; }
    if (password !== confirmPassword) { toast.error('As senhas não coincidem'); return; }
    if (!token) { toast.error('Token inválido'); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/api/admin/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success('Senha administrativa redefinida!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800/90 border-slate-700 backdrop-blur-sm">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-4">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-xl text-white flex items-center justify-center gap-2">
            <Lock className="h-5 w-5" /> Redefinir Senha Admin
          </CardTitle>
          <CardDescription className="text-slate-400">
            {success ? 'Senha redefinida!' : 'Crie uma nova senha para o painel'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="text-center space-y-4" data-testid="admin-reset-success">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-7 w-7 text-emerald-400" />
              </div>
              <p className="text-sm text-slate-300">Senha administrativa redefinida. Faça login com a nova senha.</p>
              <Button onClick={() => navigate('/admin-login')} className="bg-blue-600 hover:bg-blue-700" data-testid="admin-go-to-login">
                Ir para login admin
              </Button>
            </div>
          ) : !token ? (
            <div className="text-center space-y-4" data-testid="admin-reset-no-token">
              <p className="text-sm text-red-400">Link inválido ou expirado.</p>
              <Link to="/admin-esqueci-senha">
                <Button variant="outline" className="border-slate-600 text-slate-300">Solicitar novo link</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-reset-form">
              <div>
                <Label className="text-slate-300">Nova senha</Label>
                <Input type="password" placeholder="Mínimo 6 caracteres" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500" data-testid="admin-new-password" />
              </div>
              <div>
                <Label className="text-slate-300">Confirmar nova senha</Label>
                <Input type="password" placeholder="Repita a senha" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500" data-testid="admin-confirm-password" />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading} data-testid="admin-reset-submit">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Redefinir Senha
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
